import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { ArrowLeft, BookOpen } from 'lucide-react';

const WorkbookContent: React.FC = () => {
  const { category, filename } = useParams<{ category: string; filename: string }>();
  const [md, setMd] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!category || !filename) {
      setError('Invalid file path');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    
    const filePath = `/workbook/${category}/${filename}`;
    
    fetch(filePath)
      .then(r => {
        if (!r.ok) {
          throw new Error(`HTTP ${r.status}`);
        }
        return r.text();
      })
      .then(content => {
        // Check if the response is HTML (Vite fallback) instead of markdown
        if (content.trim().startsWith('<!doctype html>') || content.trim().startsWith('<html')) {
          throw new Error('Document not found - server returned HTML fallback');
        }
        
        // Strip frontmatter metadata from display
        let cleanContent = content;
        if (content.startsWith('---')) {
          const frontmatterEnd = content.indexOf('---', 3);
          if (frontmatterEnd !== -1) {
            cleanContent = content.substring(frontmatterEnd + 3).trim();
          }
        }
        
        setMd(cleanContent);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Failed to load workbook content:', error);
        setError(`Document not found: ${filePath}. ${error.message.includes('HTML fallback') ? 'The file may not exist in the workbook directory.' : error.message}`);
        setMd('');
        setLoading(false);
      });
  }, [category, filename]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-raised)] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-consulting-slate-blue-600 mx-auto mb-4"></div>
          <p className="text-charcoal-600">Loading workbook content...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[var(--bg-raised)]">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <Link
            to="/elevation/workbook"
            className="inline-flex items-center gap-2 text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 mb-6"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Workbook
          </Link>
          <div className="bg-[var(--coral-dim)] border-2 border-[var(--coral)] p-6">
            <h2 className="text-xl font-bold text-[var(--coral)] mb-2">Error Loading Content</h2>
            <p className="text-[var(--coral)]">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-raised)]">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/elevation/workbook"
            className="inline-flex items-center gap-2 text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Workbook
          </Link>
          <div className="flex items-center gap-3">
            <BookOpen className="h-6 w-6 text-consulting-slate-blue-600" />
            <h1 className="text-2xl font-bold text-charcoal-900">
              {filename?.replace('.md', '').replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </h1>
          </div>
        </div>

        {/* Markdown Content */}
        <div className="prose prose-lg max-w-none prose-headings:text-charcoal-900 prose-p:text-charcoal-700 prose-a:text-consulting-slate-blue-600 prose-a:hover:text-consulting-slate-blue-700 prose-strong:text-charcoal-900 prose-code:text-charcoal-800 prose-code:bg-charcoal-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-pre:bg-charcoal-900 prose-pre:text-charcoal-100 prose-blockquote:border-l-consulting-slate-blue-500 prose-blockquote:text-charcoal-600">
          <ReactMarkdown
            components={{
              h1: ({node, ...props}) => <h1 className="text-3xl font-bold text-charcoal-900 mt-8 mb-4 font-heading" {...props} />,
              h2: ({node, ...props}) => <h2 className="text-2xl font-bold text-charcoal-900 mt-6 mb-3 font-heading" {...props} />,
              h3: ({node, ...props}) => <h3 className="text-xl font-semibold text-charcoal-900 mt-4 mb-2 font-heading" {...props} />,
              p: ({node, ...props}) => <p className="text-charcoal-700 mb-4 leading-relaxed font-body" {...props} />,
              ul: ({node, ...props}) => <ul className="list-disc list-inside mb-4 space-y-2 text-charcoal-700 font-body" {...props} />,
              ol: ({node, ...props}) => <ol className="list-decimal list-inside mb-4 space-y-2 text-charcoal-700 font-body" {...props} />,
              li: ({node, ...props}) => <li className="ml-4 font-body" {...props} />,
              code: ({node, inline, ...props}: any) => 
                inline ? (
                  <code className="bg-charcoal-100 px-1.5 py-0.5 rounded text-sm font-mono text-charcoal-800" {...props} />
                ) : (
                  <code className="block bg-charcoal-900 text-charcoal-100 p-4 overflow-x-auto mb-4 font-mono text-sm" {...props} />
                ),
              pre: ({node, ...props}) => <pre className="mb-4" {...props} />,
              blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-consulting-slate-blue-500 pl-4 italic text-charcoal-600 my-4 font-body" {...props} />,
              a: ({node, ...props}) => <a className="text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 underline" {...props} />,
            }}
          >
            {md}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
};

export default WorkbookContent;

